import { create } from 'zustand';
import { api, type Notification } from '../client/api';

// =============================================================================
// Notifications store (Wave 3a, NBL_REQ_010).
//
// Current user's in-app notifications. Mark-read calls patch the local list
// optimistically-after-success (server returns 204), then re-derive the unread
// count locally to avoid a redundant refetch. Backend-offline tolerant: the
// route returns { items: [], unreadCount: 0 } when no session, so loads never
// throw on a missing user.
// =============================================================================

const DEFAULT_LIMIT = 100;

type NotificationsState = {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  unreadOnly: boolean;
  load: (opts?: { unreadOnly?: boolean }) => Promise<void>;
  setUnreadOnly: (unreadOnly: boolean) => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
};

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  unreadOnly: false,

  load: async (opts) => {
    const unreadOnly = opts?.unreadOnly ?? get().unreadOnly;
    set({ loading: true, error: null });
    try {
      const res = await api.listNotifications({ unreadOnly, limit: DEFAULT_LIMIT });
      set({ items: res.items, unreadCount: res.unreadCount, loading: false });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  setUnreadOnly: async (unreadOnly) => {
    set({ unreadOnly });
    await get().load({ unreadOnly });
  },

  markRead: async (id) => {
    try {
      await api.markNotificationRead(id);
      const nowIso = new Date().toISOString();
      set((s) => {
        const items = s.unreadOnly
          ? s.items.filter((n) => n.id !== id)
          : s.items.map((n) => (n.id === id ? { ...n, readAt: n.readAt ?? nowIso } : n));
        return { items, unreadCount: Math.max(0, s.unreadCount - 1) };
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    }
  },

  markAllRead: async () => {
    try {
      await api.markAllNotificationsRead();
      const nowIso = new Date().toISOString();
      set((s) => ({
        items: s.unreadOnly
          ? []
          : s.items.map((n) => ({ ...n, readAt: n.readAt ?? nowIso })),
        unreadCount: 0,
      }));
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    }
  },
}));
